import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function ListerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        phone: "",
        per_product_amount: "",
        password: "",
        password_confirmation: "",
        father_name: "",
        gender: "",
        dob: "",
        cnic: "",
        whatsapp: "",
        house_address: "",
        account_date: "",
        reference: "",
        account_status: true,
        account_message: "",
        opening_balance: 0,
        share_percentage: 0,
        add_listing: false,
    });

    const [files, setFiles] = useState({
        cnic_front_image: null,
        cnic_back_image: null,
        user_image: null,
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.lister);
    const [alert, setAlert] = useState(null);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "multipart/form-data",
        },
    });

    const populateForm = (item) => {
        const listerProfile = item.lister || {};
        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            phone: item.phone || "",
            per_product_amount: item.per_product_amount ?? "",
            password: "",
            password_confirmation: "",
            father_name: listerProfile.father_name || "",
            gender: listerProfile.gender || "",
            dob: listerProfile.dob || "",
            cnic: listerProfile.cnic || "",
            whatsapp: listerProfile.whatsapp || "",
            house_address: listerProfile.house_address || "",
            account_date: listerProfile.account_date || "",
            reference: listerProfile.reference || "",
            account_status: listerProfile.account_status ?? true,
            account_message: listerProfile.account_message || "",
            opening_balance: listerProfile.opening_balance ?? 0,
            share_percentage: listerProfile.share_percentage ?? 0,
            add_listing: listerProfile.add_listing ?? false,
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.lister) {
                populateForm(location.state.lister);
                setPageLoading(false);
            } else {
                const fetchLister = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/lister`,
                            { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                        );
                        if (res.data?.success) {
                            const item = res.data.data.find((l) => l.id === parseInt(id));
                            if (item) {
                                populateForm(item);
                            } else {
                                setAlert({ type: "danger", message: "Lister not found." });
                            }
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load lister details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchLister();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const payload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] !== null && formData[key] !== "") {
                payload.append(key, typeof formData[key] === "boolean" ? (formData[key] ? "1" : "0") : formData[key]);
            }
        });

        if (files.cnic_front_image) payload.append("cnic_front_image", files.cnic_front_image);
        if (files.cnic_back_image) payload.append("cnic_back_image", files.cnic_back_image);
        if (files.user_image) payload.append("user_image", files.user_image);

        let url = `${process.env.REACT_APP_API_URL}/admin/lister`;
        if (isEdit) {
            url = `${process.env.REACT_APP_API_URL}/admin/lister/${id}`;
            payload.append("_method", "PUT");
        }

        try {
            const response = await axios.post(url, payload, getAuthHeader());

            if (response.data?.success) {
                navigate("/admin/lister");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading lister data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {isEdit ? t("Edit Lister") : t("Add Lister")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t("Home")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/admin/lister" className="mc-breadcrumb-link">{t("Listers")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{isEdit ? t("Edit") : t("Add")}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate("/admin/lister")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={10} className="mx-auto">
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Header className="bg-white py-3">
                            <h5 className="mb-0 fw-bold">
                                {isEdit ? t("Update Lister Details") : t("Create New Lister")}
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Name *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Nickname *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.nickname?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Email *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.email?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Phone *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.phone?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Father Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="father_name"
                                                value={formData.father_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.father_name}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>CNIC</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="cnic"
                                                value={formData.cnic}
                                                onChange={handleChange}
                                                isInvalid={!!errors.cnic}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.cnic?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>WhatsApp</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="whatsapp"
                                                value={formData.whatsapp}
                                                onChange={handleChange}
                                                isInvalid={!!errors.whatsapp}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Per Product Amount ($)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="per_product_amount"
                                                value={formData.per_product_amount}
                                                onChange={handleChange}
                                                isInvalid={!!errors.per_product_amount}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{isEdit ? "New Password (Optional)" : "Password *"}</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                isInvalid={!!errors.password}
                                                required={!isEdit}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.password?.[0]}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {(formData.password || !isEdit) && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Confirm Password *</Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    name="password_confirmation"
                                                    value={formData.password_confirmation}
                                                    onChange={handleChange}
                                                    required={!isEdit || Boolean(formData.password)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>User Image</Form.Label>
                                            <Form.Control type="file" name="user_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>CNIC Front Image</Form.Label>
                                            <Form.Control type="file" name="cnic_front_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>CNIC Back Image</Form.Label>
                                            <Form.Control type="file" name="cnic_back_image" onChange={handleFileChange} accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>House Address</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="house_address"
                                                value={formData.house_address}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    <Button variant="secondary" onClick={() => navigate("/admin/lister")}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting ? "Saving..." : isEdit ? "Update Lister" : "Save Lister"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}